// A ready-made diagram on a slide (SD, 26 Oct 2026, David's Features 4: "ready-made diagrams like SmartArt"): one box
// holding a list of points (a line each, indented for a hierarchy's levels or a step's details) that lays itself out
// as a process, a timeline, a cycle, a hierarchy or a comparison, in a few layouts each, in the deck's theme colours
// and fonts. Adding a point lays it out again; the diagram's own bar changes its layout or colours; "Convert to boxes"
// turns it into the plain shapes, words and lines it is drawn with, to design by hand.
//
// layoutDiagram draws it as boxes (shared/slideDesign.ts SlideBox) inside its own frame, so the face, the .pptx (the
// same shapes, grouped) and the converted boxes are one drawing.
//
// Plain data only, as every slide's: no CSS, addresses or HTML.

import type { DocBody, DocNode } from "./docblocks";
import { cleanColor, type SlideBox, type SlideColor, type ThemeColorId } from "./slideDesign";
import { routeLines } from "./slidePlace";

export const DIAGRAM_KINDS = ["process", "timeline", "cycle", "hierarchy", "comparison"] as const;
export type DiagramKind = (typeof DIAGRAM_KINDS)[number];
export const DIAGRAM_NAMES: Record<DiagramKind, string> = { process: "Process", timeline: "Timeline", cycle: "Cycle", hierarchy: "Hierarchy", comparison: "Comparison" };

/** Each kind's layouts, the first its default. */
export const DIAGRAM_LAYOUTS: Record<DiagramKind, { id: string; name: string }[]> = {
  process: [
    { id: "arrows", name: "Boxes and arrows" },
    { id: "chevrons", name: "Chevrons" },
    { id: "steps", name: "Numbered steps" },
  ],
  timeline: [
    { id: "line", name: "On a line" },
    { id: "milestones", name: "Milestones" },
    { id: "vertical", name: "Down the page" },
  ],
  cycle: [
    { id: "circle", name: "Circle with arrows" },
    { id: "ring", name: "Ring" },
  ],
  hierarchy: [
    { id: "org", name: "Org chart" },
    { id: "tree", name: "Left to right" },
  ],
  comparison: [
    { id: "columns", name: "Columns" },
    { id: "versus", name: "Side by side" },
  ],
};

/** What each kind does with its points, in a line for the points editor and agents. */
export const DIAGRAM_HINTS: Record<DiagramKind, string> = {
  process: "A step a line. Indent a line under a step for its details.",
  timeline: "A date or phase a line. Indent a line under it for what happens.",
  cycle: "A stage a line. Indent a line under a stage for its details.",
  hierarchy: "A person or part a line. Indent with Tab to put it under the line above.",
  comparison: "A heading a line for each column (two to four). Indent the points under each.",
};

/** How a diagram is coloured: all in the accent, the theme's accents in turn, soft cards edged in the accent, or outlines. */
export const DIAGRAM_COLORS = ["accent", "colorful", "soft", "outline"] as const;
export type DiagramColors = (typeof DIAGRAM_COLORS)[number];
export const DIAGRAM_COLOR_NAMES: Record<DiagramColors, string> = { accent: "Accent", colorful: "Theme colors", soft: "Soft cards", outline: "Outline" };

export interface DiagramPoint {
  /** its words, on one line */
  t: string;
  /** its level, 0 at the top (a hierarchy's depth, or a step's details at 1) */
  l: number;
}

export interface SlideDiagram {
  kind: DiagramKind;
  /** one of its kind's layouts (DIAGRAM_LAYOUTS) */
  layout: string;
  points: DiagramPoint[];
  colors?: DiagramColors;
  /** its own colour in place of the accent */
  color?: SlideColor;
}

/** The most points a diagram holds, the words a point holds, and how deep its levels go. */
export const MAX_POINTS = 40;
export const MAX_POINT_TEXT = 200;
export const MAX_DEPTH = 4;
/** How many columns a comparison draws. */
export const MAX_COMPARE = 4;

const clean1 = (v: unknown, max: number) => (typeof v === "string" || typeof v === "number" ? String(v).replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim().slice(0, max) : "");

/** A diagram as kept, or null when it cannot be one. Points past the limit are left out; levels never jump more than one. */
export function cleanDiagram(raw: unknown): SlideDiagram | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const kind = (DIAGRAM_KINDS as readonly unknown[]).includes(r.kind) ? (r.kind as DiagramKind) : null;
  if (!kind) return null;
  const layout = DIAGRAM_LAYOUTS[kind].some((l) => l.id === r.layout) ? String(r.layout) : DIAGRAM_LAYOUTS[kind][0].id;
  const points: DiagramPoint[] = [];
  let prev = -1;
  for (const p of Array.isArray(r.points) ? r.points.slice(0, MAX_POINTS) : []) {
    const o = typeof p === "string" ? { t: p, l: 0 } : p && typeof p === "object" ? (p as Record<string, unknown>) : null;
    if (!o) continue;
    const t = clean1(o.t, MAX_POINT_TEXT);
    if (!t) continue;
    const l0 = typeof o.l === "number" && Number.isFinite(o.l) ? Math.max(0, Math.min(MAX_DEPTH, Math.round(o.l))) : 0;
    const l = Math.min(l0, prev + 1);
    points.push({ t, l });
    prev = l;
  }
  const d: SlideDiagram = { kind, layout, points };
  if ((DIAGRAM_COLORS as readonly unknown[]).includes(r.colors) && r.colors !== "accent") d.colors = r.colors as DiagramColors;
  const color = cleanColor(r.color);
  if (color && color !== "accent") d.color = color;
  return d;
}

/** Points from lines of words: one a line, indented by tabs, two spaces, or a list's "-", "*", "1." marks. */
export function pointsFromText(text: string): DiagramPoint[] {
  const out: DiagramPoint[] = [];
  for (const line of text.replace(/\r/g, "").split("\n")) {
    if (!line.trim()) continue;
    const lead = /^[\t ]*/.exec(line)![0];
    const tabs = (lead.match(/\t/g) ?? []).length;
    const spaces = lead.replace(/\t/g, "").length;
    const l = tabs + Math.floor(spaces / 2);
    const t = line.trim().replace(/^([-*+•–]|\d{1,2}[.)])\s+/, "");
    if (t) out.push({ t: t.slice(0, MAX_POINT_TEXT), l: Math.min(MAX_DEPTH, l) });
  }
  return cleanDiagram({ kind: "process", points: out })?.points ?? [];
}

/** Points as lines of words, indented by a tab a level: what the points editor shows. */
export const pointsToText = (points: DiagramPoint[]) => points.map((p) => "\t".repeat(p.l) + p.t).join("\n");

/** Points from a box's words or a layout's text: each paragraph or point a line, a list's levels its levels. */
export function pointsFromDoc(body: DocBody | null | undefined): DiagramPoint[] {
  const out: DiagramPoint[] = [];
  const inline = (n: DocNode): string => (n.type === "text" ? (n.text ?? "") : n.type === "hardBreak" ? " " : (n.content ?? []).map(inline).join(""));
  const walk = (nodes: DocNode[] | undefined, level: number) => {
    for (const n of nodes ?? []) {
      if (n.type === "bulletList" || n.type === "orderedList" || n.type === "taskList")
        for (const it of n.content ?? []) {
          const [first, ...rest] = it.content ?? [];
          if (first) out.push({ t: inline(first).trim(), l: level });
          walk(rest, level + 1);
        }
      else if (n.type === "paragraph" || n.type === "heading") out.push({ t: inline(n).trim(), l: level });
      else walk(n.content, level);
    }
  };
  walk(body?.content, 0);
  return cleanDiagram({ kind: "process", points: out })?.points ?? [];
}

/** The points as a tree: each top point with what is indented under it. */
export interface PointNode {
  t: string;
  kids: PointNode[];
}

export function pointTree(points: DiagramPoint[]): PointNode[] {
  const roots: PointNode[] = [];
  const stack: { l: number; n: PointNode }[] = [];
  for (const p of points) {
    const n: PointNode = { t: p.t, kids: [] };
    while (stack.length && stack[stack.length - 1].l >= p.l) stack.pop();
    if (stack.length) stack[stack.length - 1].n.kids.push(n);
    else roots.push(n);
    stack.push({ l: p.l, n });
  }
  return roots;
}

/** Every line under a node, in order, flattened (a step's details). */
const linesUnder = (n: PointNode): string[] => n.kids.flatMap((k) => [k.t, ...linesUnder(k)]);

// ---------------------------------------------------------------------------
// laying it out: boxes in the diagram's own pixels (0, 0 its top left), the same everywhere it is drawn

/** A part of a diagram as drawn: a box with an id of its own inside the diagram ("p0", "p1"), z in drawing order. */
export type DiagramPart = SlideBox;

const ACCENTS: ThemeColorId[] = ["accent", "accent2", "accent3", "accent4"];
const r1 = (n: number) => Math.round(n * 10) / 10;

interface Look {
  fill?: SlideColor | null;
  line?: SlideColor | null;
  lineW?: number;
  color?: SlideColor;
}

/** A node's look, for its colours: its place among its siblings (i), or its depth in a hierarchy. */
function nodeLook(d: SlideDiagram, i: number): Look {
  const base = d.color ?? "accent";
  switch (d.colors ?? "accent") {
    case "colorful":
      return { fill: d.color && i === 0 ? d.color : ACCENTS[i % 4] };
    case "soft":
      return { fill: "surface", line: base, lineW: 3, color: "text" };
    case "outline":
      return { fill: null, line: base, lineW: 4, color: "text" };
    default:
      return { fill: base };
  }
}

/** The colour of what marks a node on the slide (a dot, a numbered circle), and of a label's heading. */
const markColor = (d: SlideDiagram, i: number): SlideColor => (d.colors === "colorful" ? (d.color && i === 0 ? d.color : ACCENTS[i % 4]) : (d.color ?? "accent"));

/**
 * The largest size (to the most) at which these lines fit the room, as the face lays words: about half an em a
 * character, lines 1.25 apart. The face still shrinks words that run over (a box's own fit); this keeps a diagram's
 * nodes one size.
 */
export function sizeToFit(w: number, h: number, lines: { t: string; k?: number }[], most: number, least = 14): number {
  let s = most;
  while (s > least && !(textHeight(w, lines, s) <= h)) s -= 1;
  return s;
}

/** How tall these lines stand at a size, wrapped to w (Infinity when a word is wider than the room). */
export function textHeight(w: number, lines: { t: string; k?: number }[], size: number): number {
  let used = 0;
  for (const l of lines) {
    const s = size * (l.k ?? 1);
    const per = Math.max(1, Math.floor(w / (s * 0.56)));
    let rows = 1;
    let run = 0;
    for (const wd of l.t.split(/\s+/)) {
      // a word never breaks: one wider than the room does not fit at this size
      if (wd.length > per) return Infinity;
      const n = wd.length + (run ? 1 : 0);
      if (run + n > per && run > 0) {
        rows++;
        run = wd.length;
      } else run += n;
    }
    used += rows * s * 1.3;
  }
  return used;
}

const para = (text: string, marks?: DocNode["marks"]): DocNode => ({ type: "paragraph", content: text ? [{ type: "text", text, ...(marks?.length ? { marks } : {}) }] : [] });

/** A node's words: its heading (bold when lines follow), then its lines smaller, plain or as points. */
function wordsOf(head: string, subs: string[], subSize: number | null, opts: { bullets?: boolean; plainHead?: boolean; boldHead?: boolean } = {}): DocBody {
  const content: DocNode[] = [para(head, (subs.length || opts.boldHead) && !opts.plainHead ? [{ type: "bold" }] : undefined)];
  const sized = subSize ? [{ type: "textStyle", attrs: { size: subSize } }] : undefined;
  if (subs.length) {
    if (opts.bullets) content.push({ type: "bulletList", content: subs.map((s) => ({ type: "listItem", content: [para(s, sized)] })) });
    else for (const s of subs) content.push(para(s, sized));
  }
  return { type: "doc", content };
}

/** Boxes made in order, each with its own id and layer. */
class Parts {
  list: DiagramPart[] = [];
  add(b: Omit<DiagramPart, "id" | "z">): string {
    const id = `p${this.list.length}`;
    this.list.push({ ...b, id, z: this.list.length } as DiagramPart);
    return id;
  }
}

/**
 * The diagram drawn as boxes in its frame (w by h): shapes with words, words on the slide, lines and arrows (attached
 * to what they join, as connectors). An empty diagram draws nothing.
 */
export function layoutDiagram(d: SlideDiagram, w: number, h: number): DiagramPart[] {
  const tree = pointTree(d.points);
  if (!tree.length || w < 20 || h < 20) return [];
  const P = new Parts();
  const go = LAYOUTS[d.kind]?.[d.layout] ?? LAYOUTS[d.kind][DIAGRAM_LAYOUTS[d.kind][0].id];
  go(P, d, tree, w, h);
  return routeLines(P.list);
}

type LayoutFn = (P: Parts, d: SlideDiagram, tree: PointNode[], w: number, h: number) => void;

const MUTED: SlideColor = "muted";

// ---------------------------------------------------------------------------
// process

const processArrows: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const subs = tree.some((t) => t.kids.length);
  const gap = Math.max(24, Math.min(84, w * 0.05));
  const bw = (w - gap * (n - 1)) / n;
  const pad = 28;
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.72 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(bw - pad * 2, h - pad * 2, ls, subs ? 36 : 40)));
  // all one height: the tallest words' room, never less than a calm card, never more than the frame
  const need = Math.max(...lines.map((ls) => textHeight(bw - pad * 2, ls, size))) + pad * 2 + 8;
  const bh = Math.min(h, Math.max(need, Math.min(bw * 0.62, h), Math.min(h, 120)));
  const y = (h - bh) / 2;
  tree.forEach((t, i) => {
    const x = i * (bw + gap);
    P.add({ kind: "shape", shape: "round", x: r1(x), y: r1(y), w: r1(bw), h: r1(bh), radius: r1(Math.min(bw, bh) * 0.1), ...nodeLook(d, i), text: wordsOf(t.t, linesUnder(t), subs ? Math.round(size * 0.72) : null, { boldHead: subs }), size, ...(subs ? {} : { weight: 600 }), lineH: 1.25 });
    if (i < n - 1) {
      const aw = gap * 0.62;
      const ah = Math.min(aw * 1.15, bh * 0.4);
      P.add({ kind: "shape", shape: "arrow", x: r1(x + bw + (gap - aw) / 2), y: r1(h / 2 - ah / 2), w: r1(aw), h: r1(ah), fill: MUTED, opacity: 0.55 });
    }
  });
};

const processChevrons: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const subs = tree.some((t) => t.kids.length);
  const ch0 = subs ? Math.min(h * 0.36, 150) : Math.min(h, 150);
  // each chevron's point fits the next one's notch, a small gap between
  const gap = Math.max(8, w * 0.008);
  const ch = Math.min(ch0, (w / n) * 0.6);
  const d0 = ch * 0.5;
  const cw = (w + (n - 1) * (d0 - gap)) / n;
  const step = cw - d0 + gap;
  const subH = subs ? Math.max(...tree.map((t) => textHeight(cw - d0, linesUnder(t).map((x) => ({ t: x })), 26) || 0)) : 0;
  const cy = subs ? Math.max(0, (h - ch - 24 - Math.min(subH, h - ch - 24)) / 2) : (h - ch) / 2;
  const inner = cw - d0 * 2 - 16;
  // a shape's words sit 24 pixels in from each side (shapes' insets): sized for the room left
  const size = Math.min(...tree.map((t) => sizeToFit(Math.max(40, inner - 16), ch - 56, [{ t: t.t }], 36)));
  tree.forEach((t, i) => {
    P.add({ kind: "shape", shape: "chevron", x: r1(i * step), y: r1(cy), w: r1(cw), h: r1(ch), ...nodeLook(d, i), text: wordsOf(t.t, [], null), size, weight: 600, lineH: 1.15 });
    const under = linesUnder(t);
    if (under.length) {
      const tw = step - gap * 2;
      const sub = Math.min(...tree.map((x) => sizeToFit(tw, h - ch - 28, linesUnder(x).map((s) => ({ t: s })), 26)));
      P.add({ kind: "text", x: r1(i * step + d0 * 0.5), y: r1(cy + ch + 24), w: r1(tw - d0 * 0.2), h: r1(h - cy - ch - 24), text: { type: "doc", content: [{ type: "bulletList", content: under.map((s) => ({ type: "listItem", content: [para(s)] })) }] }, size: sub, color: "text", lineH: 1.3 });
    }
  });
};

const processSteps: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const col = w / n;
  const subs = tree.some((t) => t.kids.length);
  const dia = Math.max(48, Math.min(col * 0.42, h * (subs ? 0.3 : 0.4), 150));
  const tw0 = col - Math.max(16, col * 0.1);
  const size0 = Math.min(...tree.map((t) => sizeToFit(tw0, h - dia - 24, [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.78 }))], 32)));
  const textH = Math.max(...tree.map((t) => textHeight(tw0, [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.78 }))], size0)));
  const cy = Math.max(dia / 2 + 4, (h - dia - 20 - Math.min(textH, h - dia - 20)) / 2 + dia / 2);
  if (n > 1) P.add({ kind: "line", x: r1(col / 2), y: r1(cy), w: r1(col * (n - 1)), h: 0, line: MUTED, lineW: 4, opacity: 0.6 });
  const tw = col - Math.max(16, col * 0.1);
  const top = cy + dia / 2 + 20;
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.78 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(tw, h - top, ls, 32)));
  tree.forEach((t, i) => {
    const x = col * i + col / 2;
    P.add({ kind: "shape", shape: "ellipse", x: r1(x - dia / 2), y: r1(cy - dia / 2), w: r1(dia), h: r1(dia), ...nodeLook(d, i), text: wordsOf(String(i + 1), [], null), size: Math.round(dia * 0.4), weight: 700, lineH: 1 });
    P.add({ kind: "text", x: r1(x - tw / 2), y: r1(top), w: r1(tw), h: r1(Math.max(40, h - top)), text: wordsOf(t.t, linesUnder(t), Math.round(size * 0.78)), size, align: "center", color: "text", ...(t.kids.length ? {} : { weight: 600 }), lineH: 1.25 });
  });
};

// ---------------------------------------------------------------------------
// timeline

const timelineLine: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const ly = h / 2;
  const dot = Math.max(18, Math.min(h * 0.06, 34));
  P.add({ kind: "line", x: 0, y: r1(ly), w: r1(w), h: 0, line: MUTED, lineW: 4, tail: "arrow", opacity: 0.7 });
  const span = w - dot * 2;
  const slot = span / n;
  const lw = Math.min(slot * 1.7, 440, w);
  const stem = Math.max(14, h * 0.05);
  const room = ly - dot / 2 - stem - 8;
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.8 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(lw, room, ls, 32)));
  tree.forEach((t, i) => {
    const x = dot + slot * (i + 0.5);
    const up = i % 2 === 0;
    P.add({ kind: "line", x: r1(x), y: r1(up ? ly - dot / 2 - stem : ly + dot / 2), w: 0, h: r1(stem), line: markColor(d, i), lineW: 3 });
    P.add({ kind: "shape", shape: "ellipse", x: r1(x - dot / 2), y: r1(ly - dot / 2), w: r1(dot), h: r1(dot), fill: d.colors === "outline" || d.colors === "soft" ? "background" : markColor(d, i), line: d.colors === "outline" || d.colors === "soft" ? markColor(d, i) : null, lineW: 4 });
    const lx = Math.max(0, Math.min(w - lw, x - lw / 2));
    const runs = wordsOf(t.t, linesUnder(t), Math.round(size * 0.8), { plainHead: true });
    // the date or phase in its colour, what happens under it in the text's
    const head = runs.content[0];
    if (head.content?.[0]) head.content[0] = { ...head.content[0], marks: [{ type: "bold" }, { type: "textStyle", attrs: { color: markColor(d, i) } }] };
    P.add({ kind: "text", x: r1(lx), y: r1(up ? 0 : ly + dot / 2 + stem + 8), w: r1(lw), h: r1(room), text: runs, size, align: "center", valign: up ? "bottom" : "top", color: "text", lineH: 1.25 });
  });
};

const timelineMilestones: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const subs = tree.some((t) => t.kids.length);
  const slot = w / n;
  const pw = Math.min(slot * 0.82, 320);
  const ph = Math.max(84, Math.min(h * 0.2, 100));
  const tw = slot - Math.max(16, slot * 0.1);
  const sub = subs ? Math.min(...tree.map((t) => sizeToFit(tw, h - ph - 26, linesUnder(t).map((s) => ({ t: s })), 28))) : 0;
  const subH = subs ? Math.min(h - ph - 26, Math.max(...tree.map((t) => textHeight(tw, linesUnder(t).map((s) => ({ t: s })), sub)))) : 0;
  const ly = subs ? Math.max(ph / 2 + 4, (h - ph - 22 - subH) / 2 + ph / 2) : h / 2;
  P.add({ kind: "line", x: 0, y: r1(ly), w: r1(w), h: 0, line: MUTED, lineW: 4, opacity: 0.6 });
  const size = Math.min(...tree.map((t) => sizeToFit(pw - 64, ph - 52, [{ t: t.t }], 30)));
  const top = ly + ph / 2 + 22;
  tree.forEach((t, i) => {
    const x = slot * (i + 0.5);
    P.add({ kind: "shape", shape: "round", x: r1(x - pw / 2), y: r1(ly - ph / 2), w: r1(pw), h: r1(ph), radius: r1(ph / 2), ...nodeLook(d, i), text: wordsOf(t.t, [], null), size, weight: 650, lineH: 1.1 });
    const under = linesUnder(t);
    if (under.length) P.add({ kind: "text", x: r1(x - tw / 2), y: r1(top), w: r1(tw), h: r1(h - top), text: { type: "doc", content: under.map((s) => para(s)) }, size: sub, align: "center", color: "text", lineH: 1.3 });
  });
};

const timelineVertical: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const dot = Math.max(16, Math.min(h / n * 0.3, 30));
  const lx = dot;
  const row = h / n;
  P.add({ kind: "line", x: r1(lx), y: r1(row * 0.5), w: 0, h: r1(row * (n - 1)), line: MUTED, lineW: 4, opacity: 0.6 });
  const tx = lx + dot * 1.6;
  const tw = w - tx;
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.82 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(tw, row - 8, ls, 32)));
  tree.forEach((t, i) => {
    const cy = row * (i + 0.5);
    P.add({ kind: "shape", shape: "ellipse", x: r1(lx - dot / 2), y: r1(cy - dot / 2), w: r1(dot), h: r1(dot), fill: d.colors === "outline" || d.colors === "soft" ? "background" : markColor(d, i), line: d.colors === "outline" || d.colors === "soft" ? markColor(d, i) : null, lineW: 4 });
    const runs = wordsOf(t.t, linesUnder(t), Math.round(size * 0.82), { plainHead: true });
    const head = runs.content[0];
    if (head.content?.[0]) head.content[0] = { ...head.content[0], marks: [{ type: "bold" }, { type: "textStyle", attrs: { color: markColor(d, i) } }] };
    P.add({ kind: "text", x: r1(tx), y: r1(cy - row / 2 + 4), w: r1(tw), h: r1(row - 8), text: runs, size, valign: "middle", color: "text", lineH: 1.25 });
  });
};

// ---------------------------------------------------------------------------
// cycle

function ringPlaces(n: number, w: number, h: number, node: { w: number; h: number }) {
  const rx = Math.max(0, w / 2 - node.w / 2);
  const ry = Math.max(0, h / 2 - node.h / 2);
  return Array.from({ length: n }, (_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { a, x: w / 2 + rx * Math.cos(a), y: h / 2 + ry * Math.sin(a) };
  });
}

const cycleCircle: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const k = n <= 3 ? 0.36 : n <= 4 ? 0.34 : n <= 6 ? 0.28 : 0.22;
  const side = Math.max(80, Math.min(w, h) * k);
  const node = { w: Math.min(side * 1.5, w * 0.34), h: side };
  const at = ringPlaces(n, w, h, node);
  const subs = tree.some((t) => t.kids.length);
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.75 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(node.w - 64, node.h - 56, ls, 32)));
  tree.forEach((t, i) => P.add({ kind: "shape", shape: "round", x: r1(at[i].x - node.w / 2), y: r1(at[i].y - node.h / 2), w: r1(node.w), h: r1(node.h), radius: r1(node.h * 0.5), ...nodeLook(d, i), text: wordsOf(t.t, linesUnder(t), subs ? Math.round(size * 0.75) : null), size, ...(subs ? {} : { weight: 600 }), lineH: 1.2 }));
  if (n < 2) return;
  // an arrow from each stage's edge to the next one's, a little apart from both
  const edge = (ux: number, uy: number) => 1 / Math.sqrt((ux / (node.w / 2)) ** 2 + (uy / (node.h / 2)) ** 2);
  const gap = Math.max(14, Math.min(node.w, node.h) * 0.12);
  for (let i = 0; i < n; i++) {
    const a = at[i];
    const b = at[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ux = (b.x - a.x) / len;
    const uy = (b.y - a.y) / len;
    const p = { x: a.x + ux * (edge(ux, uy) + gap), y: a.y + uy * (edge(ux, uy) + gap) };
    const q = { x: b.x - ux * (edge(ux, uy) + gap), y: b.y - uy * (edge(ux, uy) + gap) };
    if ((q.x - p.x) * ux + (q.y - p.y) * uy < 24) continue;
    P.add({ kind: "line", x: r1(Math.min(p.x, q.x)), y: r1(Math.min(p.y, q.y)), w: r1(Math.abs(q.x - p.x)), h: r1(Math.abs(q.y - p.y)), ...(q.x < p.x ? { flipH: true } : {}), ...(q.y < p.y ? { flipV: true } : {}), line: MUTED, lineW: 4, tail: "arrow", opacity: 0.75 });
  }
};

const cycleRing: LayoutFn = (P, d, tree, w, h) => {
  const n = tree.length;
  const k = n <= 4 ? 0.3 : n <= 6 ? 0.25 : 0.2;
  const side = Math.max(72, Math.min(w, h) * k);
  const node = { w: Math.min(side * 1.7, w * 0.36), h: side };
  const rx = w / 2 - node.w / 2;
  const ry = h / 2 - node.h / 2;
  P.add({ kind: "shape", shape: "ellipse", x: r1(w / 2 - rx), y: r1(h / 2 - ry), w: r1(rx * 2), h: r1(ry * 2), fill: null, line: MUTED, lineW: 6, opacity: 0.45 });
  const at = ringPlaces(n, w, h, node);
  const subs = tree.some((t) => t.kids.length);
  const lines = tree.map((t) => [{ t: t.t }, ...linesUnder(t).map((s) => ({ t: s, k: 0.75 }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(node.w - 56, node.h - 56, ls, 30)));
  tree.forEach((t, i) => {
    P.add({ kind: "shape", shape: "round", x: r1(at[i].x - node.w / 2), y: r1(at[i].y - node.h / 2), w: r1(node.w), h: r1(node.h), radius: r1(node.h * 0.22), ...nodeLook(d, i), text: wordsOf(t.t, linesUnder(t), subs ? Math.round(size * 0.75) : null), size, ...(subs ? {} : { weight: 600 }), lineH: 1.2 });
  });
};

// ---------------------------------------------------------------------------
// hierarchy

interface Placed {
  n: PointNode;
  depth: number;
  /** its place across (top down) or down (left to right), in leaf slots */
  at: number;
  kids: Placed[];
}

function placeTree(roots: PointNode[]): { all: Placed[]; leaves: number; depth: number } {
  const all: Placed[] = [];
  let leaf = 0;
  let deepest = 0;
  const walk = (n: PointNode, depth: number): Placed => {
    deepest = Math.max(deepest, depth);
    const p: Placed = { n, depth, at: 0, kids: [] };
    all.push(p);
    if (!n.kids.length) p.at = leaf++ + 0.5;
    else {
      p.kids = n.kids.map((k) => walk(k, depth + 1));
      p.at = (p.kids[0].at + p.kids[p.kids.length - 1].at) / 2;
    }
    return p;
  };
  roots.forEach((r) => walk(r, 0));
  return { all, leaves: Math.max(1, leaf), depth: deepest + 1 };
}

function hierarchy(P: Parts, d: SlideDiagram, tree: PointNode[], w: number, h: number, across: boolean) {
  const { all, leaves, depth } = placeTree(tree);
  const slot = (across ? w : h) / leaves;
  const band = (across ? h : w) / depth;
  // nodes as wide as their slot allows, and room between levels for the connectors
  const nw = across ? Math.min(slot * 0.86, 380) : Math.min(band * 0.72, 420);
  const nh = across ? Math.min(band * 0.6, 150) : Math.min(slot * 0.8, 130);
  const size = Math.min(...all.map((p) => sizeToFit(nw - 56, nh - 54, [{ t: p.n.t }], 30)));
  const ids = new Map<Placed, string>();
  for (const p of all) {
    const cx = across ? p.at * slot : band * p.depth + band / 2;
    const cy = across ? band * p.depth + band / 2 : p.at * slot;
    // the top in the colour itself, the levels under it as cards edged in it (in Theme colors, a colour a level)
    const look: Look = d.colors === "colorful" ? { fill: (p.depth === 0 && d.color ? d.color : ACCENTS[p.depth % 4]) as SlideColor } : (d.colors ?? "accent") === "accent" && p.depth > 0 ? { fill: "surface", line: d.color ?? "accent", lineW: 3, color: "text" } : nodeLook(d, 0);
    ids.set(p, P.add({ kind: "shape", shape: "round", x: r1(cx - nw / 2), y: r1(cy - nh / 2), w: r1(nw), h: r1(nh), radius: r1(Math.min(nw, nh) * 0.16), ...look, text: wordsOf(p.n.t, [], null), size, weight: p.depth === 0 ? 700 : 600, lineH: 1.15 }));
  }
  for (const p of all)
    for (const k of p.kids) {
      const a = P.list.find((b) => b.id === ids.get(p))!;
      const z = P.list.find((b) => b.id === ids.get(k))!;
      const from = across ? { x: a.x + a.w / 2, y: a.y + a.h } : { x: a.x + a.w, y: a.y + a.h / 2 };
      const to = across ? { x: z.x + z.w / 2, y: z.y } : { x: z.x, y: z.y + z.h / 2 };
      P.add({ kind: "line", x: r1(Math.min(from.x, to.x)), y: r1(Math.min(from.y, to.y)), w: r1(Math.abs(to.x - from.x)), h: r1(Math.abs(to.y - from.y)), ...(to.x < from.x ? { flipH: true } : {}), ...(to.y < from.y ? { flipV: true } : {}), line: MUTED, lineW: 3, elbow: true, from: { box: ids.get(p)!, at: across ? "bottom" : "right" }, to: { box: ids.get(k)!, at: across ? "top" : "left" }, opacity: 0.8 });
    }
  // connectors under the nodes
  P.list.sort((a, b) => (a.kind === "line" ? 0 : 1) - (b.kind === "line" ? 0 : 1) || a.z - b.z);
  P.list.forEach((b, i) => (b.z = i));
}

// ---------------------------------------------------------------------------
// comparison

const compareColumns: LayoutFn = (P, d, tree, w, h) => {
  const cols = tree.slice(0, MAX_COMPARE);
  const n = cols.length;
  const gap = Math.max(24, w * 0.035);
  const cw = (w - gap * (n - 1)) / n;
  const hh = Math.max(88, Math.min(h * 0.2, 112));
  const head = Math.min(...cols.map((c) => sizeToFit(cw - 56, hh - 54, [{ t: c.t }], 36)));
  const body = Math.min(...cols.map((c) => sizeToFit(cw - 80, h - hh - 64, linesUnder(c).map((s) => ({ t: s, k: 1.08 })), 32)));
  const need = Math.max(0, ...cols.map((c) => textHeight(cw - 80, linesUnder(c).map((s) => ({ t: s, k: 1.08 })), body)));
  const ph = Math.min(h, Math.max(hh + need + 72, h * 0.55));
  const y = (h - ph) / 2;
  cols.forEach((c, i) => {
    const x = i * (cw + gap);
    P.add({ kind: "shape", shape: "round", x: r1(x), y: r1(y), w: r1(cw), h: r1(ph), radius: 20, fill: "surface", line: d.colors === "outline" ? (d.color ?? "accent") : null, lineW: 3 });
    P.add({ kind: "shape", shape: "round", x: r1(x), y: r1(y), w: r1(cw), h: r1(hh), radius: 20, ...nodeLook(d, i), ...(d.colors === "soft" ? { fill: "background" } : {}), text: wordsOf(c.t, [], null), size: head, weight: 700, lineH: 1.15 });
    const under = linesUnder(c);
    if (under.length) P.add({ kind: "text", x: r1(x + 32), y: r1(y + hh + 28), w: r1(cw - 64), h: r1(ph - hh - 44), text: { type: "doc", content: [{ type: "bulletList", content: under.map((s) => ({ type: "listItem", content: [para(s)] })) }] }, size: body, color: "text", lineH: 1.35, after: Math.round(body * 0.35) });
  });
};

const compareVersus: LayoutFn = (P, d, tree, w, h) => {
  const cols = tree.slice(0, 2);
  if (cols.length < 2) return compareColumns(P, d, tree, w, h);
  const gap = Math.max(56, w * 0.07);
  const cw = (w - gap) / 2;
  const vs = Math.min(gap * 1.45, 112, h * 0.3);
  // each side a panel with its heading and points in it, in words that read on its fill
  const pad = 48 + (vs - gap) / 2;
  const lines = cols.map((c) => [{ t: c.t, k: 1.3 }, ...linesUnder(c).map((s) => ({ t: s }))]);
  const size = Math.min(...lines.map((ls) => sizeToFit(cw - pad - 56, h - 80, ls, 36)));
  const need = Math.max(...lines.map((ls) => textHeight(cw - pad - 56, ls, size))) + 96;
  const ph = Math.min(h, Math.max(need, h * 0.6, vs + 40));
  const y = (h - ph) / 2;
  cols.forEach((c, i) => {
    const x = i * (cw + gap);
    const words: DocBody = { type: "doc", content: [para(c.t, [{ type: "bold" }, { type: "textStyle", attrs: { size: Math.round(size * 1.3) } }])] };
    const under = linesUnder(c);
    if (under.length) words.content.push({ type: "bulletList", content: under.map((s) => ({ type: "listItem", content: [para(s)] })) });
    P.add({ kind: "shape", shape: "round", x: r1(x), y: r1(y), w: r1(cw), h: r1(ph), radius: 24, ...nodeLook(d, i), text: words, size, align: "left", valign: "middle", lineH: 1.35, after: Math.round(size * 0.35) });
  });
  // where the two meet: a round mark that says it
  P.add({ kind: "shape", shape: "ellipse", x: r1(w / 2 - vs / 2), y: r1(h / 2 - vs / 2), w: r1(vs), h: r1(vs), fill: "background", line: d.colors === "outline" || d.colors === "soft" ? (d.color ?? "accent") : null, lineW: 4, text: wordsOf("vs", [], null), size: Math.round(vs * 0.34), weight: 700, color: "text", lineH: 1 });
};

const LAYOUTS: Record<DiagramKind, Record<string, LayoutFn>> = {
  process: { arrows: processArrows, chevrons: processChevrons, steps: processSteps },
  timeline: { line: timelineLine, milestones: timelineMilestones, vertical: timelineVertical },
  cycle: { circle: cycleCircle, ring: cycleRing },
  hierarchy: { org: (P, d, t, w, h) => hierarchy(P, d, t, w, h, true), tree: (P, d, t, w, h) => hierarchy(P, d, t, w, h, false) },
  comparison: { columns: compareColumns, versus: compareVersus },
};

/** A diagram's parts placed on the slide (its frame's offset added), each with an id of its own, grouped: "Convert to boxes". */
export function diagramToBoxes(d: SlideDiagram, frame: { x: number; y: number; w: number; h: number; z: number }, newId: () => string, group: string): SlideBox[] {
  const parts = layoutDiagram(d, frame.w, frame.h);
  const ids = new Map(parts.map((p) => [p.id, newId()]));
  return parts.map((p, i) => {
    const b: SlideBox = { ...p, id: ids.get(p.id)!, x: r1(p.x + frame.x), y: r1(p.y + frame.y), z: frame.z + i / 1000, group };
    if (p.from) b.from = { ...p.from, box: ids.get(p.from.box) ?? p.from.box };
    if (p.to) b.to = { ...p.to, box: ids.get(p.to.box) ?? p.to.box };
    return b;
  });
}

/** A new diagram's place and size on a 1600 by 900 slide, by kind (a hierarchy taller, a timeline wider). */
export const DIAGRAM_SIZE: Record<DiagramKind, { w: number; h: number }> = {
  process: { w: 1360, h: 300 },
  timeline: { w: 1400, h: 440 },
  cycle: { w: 900, h: 600 },
  hierarchy: { w: 1200, h: 560 },
  comparison: { w: 1280, h: 560 },
};
